import { useState, useRef, useEffect, useCallback } from "react";
import { Box, Button, CircularProgress, Typography, useMediaQuery, Snackbar, Alert } from "@mui/material";
import Logo from "../../../components/common/Logo/Logo";
import InputWithLabel from "../../../components/common/InputWithLabel/InputWithLabel";
import LoaderButton from "../../../components/common/LoaderButton/LoaderButton";
import { AiOutlineMail } from "react-icons/ai";
import { AiOutlineClose } from "react-icons/ai";
import { IconButton } from "@mui/material";
import PropTypes from "prop-types";
import { apiFetch } from "../../../utils/api";
import styles from "./SignIn.module.css";
import * as Sentry from "@sentry/react";

const SignIn = ({ className = "", onClose, onSignInSuccess, emailPrefill = "", create = false }) => {
  const [email, setEmail] = useState(emailPrefill);
  const [isCodeConfirmation, setIsCodeConfirmation] = useState(false);
  const [code, setCode] = useState(new Array(6).fill(""));
  const [isAppLive, setIsAppLive] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const inputRefs = useRef([]);
  const isMobile = useMediaQuery("(max-width: 768px)");
  const emailRef = useRef(emailPrefill); 

  const [loginToken, setLoginToken] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  const emailInputRef = useRef(null); 

  useEffect(() => {
    if (emailInputRef.current) {
      emailInputRef.current.focus(); 
    }
  }, []); 

  const closeModal = () => {
    if (onClose) onClose();
  };

  const handleSignInClick = async () => {
    if (!emailRef.current) {
      setErrorMessage("Please enter your email.");
      return;
    }
  
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(emailRef.current)) {
      setErrorMessage("Please enter a valid email address.");
      return;
    }

    setIsProcessing(true);
    try {
      const res = await apiFetch("/auth/login-email", {
        method: "POST",
        body: JSON.stringify({
          email: emailRef.current,
          text: "Welcome to MomentoMaster — where your greatest moments become timeless digital Memories.",
          imageurl: "http://cdn.mcauto-images-production.sendgrid.net/951eea0e9010a1ad/f7f3abff-120e-450d-aa4c-836607786b37/1445x515.png",
          partner: "MomentoMaster",
        }),
      });
  
      if (res.tokens) {
        setLoginToken(res.tokens);
        setEmail(emailRef.current);
        setIsCodeConfirmation(true);
        setErrorMessage("");
      } else {
        setErrorMessage("Failed to send login email.");
      }
    } catch (error) {
      Sentry.captureException(error);
      setErrorMessage("Something went wrong. Please try again.");
      console.error("❌ Login Email Error:", error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleEmailChange = (value) => {
    emailRef.current = value; // Keep updating the ref, not state
  };

  // const handleChangeCode = (index, value) => {
  //   if (/^[0-9]*$/.test(value)) {
  //     const newCode = [...code];
  //     newCode[index] = value.slice(-1); // Only takes the last entered character
  //     setCode(newCode);

  //     // Move focus to next input AFTER state update
  //     if (value && index < 5) {
  //       setTimeout(() => {
  //         if (inputRefs.current[index + 1]) {
  //           inputRefs.current[index + 1].focus();
  //         }
  //       }, 0); // Timeout of 0 ensures this runs after React updates the state
  //     }
  //   }
  // };

  const handleChangeCode = (index, value) => {
    const newCode = [...code];
    newCode[index] = value.slice(-1); // allow emptying or setting 1 digit
    setCode(newCode);
  
    if (value && index < 5) {
      setTimeout(() => {
        inputRefs.current[index + 1]?.focus();
      }, 0);
    }
  };

  const handleConfirmCode = async () => {
    if (code.some((char) => char === "")) {
      setErrorMessage("Please enter the full 6-digit code.");
      return;
    }

    setIsProcessing(true);
    try {
      const res = await apiFetch("/auth/login-code", {
        method: "POST",
        body: JSON.stringify({
          token: loginToken,
          code: code.join(""),
        }),
      }, true); // useAuth: true if you want to store token after
  
      if (res.status === 200 || res.tokens?.access?.token) {
        localStorage.setItem("authToken", res.tokens?.access?.token);
        localStorage.setItem("authRefreshToken", res.tokens?.refresh?.token);
        onSignInSuccess();
      } else {
        setErrorMessage("Invalid code. Please try again.");
      }
    } catch (error) {
      Sentry.captureException(error);
      setErrorMessage("Invalid code. Please try again.");
      console.error("❌ Login Code Error:", error);
    } finally {
      setIsProcessing(false);
    }
  };

  const SignInScreen = () => (
    <>
      <IconButton
        onClick={closeModal}
        sx={{
          position: "absolute",
          top: "10px",
          right: "10px",
          color: "#ff9500",
        }}
      >
        <AiOutlineClose />
      </IconButton>
      <Box className={styles.logo1Parent}>
        <Logo group="/group.svg" group1="/group-1.svg" />
        <img className={styles.paypangealogoIcon} alt="" src="/paypangealogo1.svg" />
      </Box>
      <Box className={styles.signIn1}>{create ? 'Create memory' : 'Sign in'}</Box>

      <Box className={(!isMobile && isAppLive) ? styles.desktopContainer : styles.mobileContainer}>
        <Box className={styles.inputSection}>
          <InputWithLabel
            label="Enter your email"
            placeholder="Enter your email"
            icon={AiOutlineMail}
            onChange={handleEmailChange}
            inputRef={emailInputRef}
            defaultValue={emailPrefill}
          />

          <LoaderButton
            isProcessing={isProcessing}
            onClick={handleSignInClick}
            confirmText="Sign in"
            borderRadius="50px"
            width="280px"
          />
        </Box>

        {isAppLive && (
          <>
            <Box className={isMobile ? styles.horizontalDivider : styles.verticalDivider}>
              <Typography className={styles.orText}>OR</Typography>
            </Box>

            {isMobile ? (
              <Button
                variant="contained"
                className={styles.authenticateButton}
                sx={{
                  backgroundColor: "#ff9500",
                  color: "#fff",
                  textTransform: "none",
                  borderRadius: "50px",
                  width: "240px",
                  "&:hover": { backgroundColor: "#ff9500" },
                }}
              >
                Authenticate in your PayPangea app
              </Button>
            ) : (
              <Box className={styles.qr}>
                <Box className={styles.scanQrCode}>Scan the QR code with your PayPangea app</Box>
                <img className={styles.bitmapIcon} alt="" src="/bitmap@2x.png" />
              </Box>
            )}
          </>
        )}
      </Box>
    </>
  );

  const CodeConfirmationScreen = () => {
    useEffect(() => {
      inputRefs.current[0]?.focus();
    }, []);
  
    const handlePaste = (e) => {
      e.preventDefault();
      const paste = e.clipboardData?.getData("text")?.trim();
      if (/^\d{6}$/.test(paste)) {
        const newCode = paste.split("");
        console.log(newCode);
        setCode(newCode);
        setTimeout(() => {
          inputRefs.current[5]?.focus();
        }, 0);
      }
    };
  
    const handleClear = () => {
      setCode(new Array(6).fill(""));
      inputRefs.current[0]?.focus();
    };
  
    const handleBack = () => {
      setIsCodeConfirmation(false);
      setCode(new Array(6).fill(""));
    };
  
    return (
      <>
        <IconButton
          onClick={closeModal}
          sx={{ position: "absolute", top: "10px", right: "10px", color: "#ff9500" }}
        >
          <AiOutlineClose />
        </IconButton>
  
        <Box className={styles.logo1Parent}>
          <Logo group="/group.svg" group1="/group-1.svg" />
          <img className={styles.paypangealogoIcon} alt="" src="/paypangealogo1.svg" />
        </Box>
  
        <Box className={styles.title}>
          <Box className={styles.signIn1}>Confirm</Box>
          <Typography className={styles.description}>
            We have sent a one-time code to your email: <b>{email}</b>
          </Typography>
        </Box>
  
        <Box sx={{ display: "flex", gap: "16px" }}>
          <Button
            onClick={handleBack}
            variant="outlined"
            sx={{ width: "140px", borderRadius: "50px", borderColor: "#ff9500", color: "#ff9500" }}
          >
            Back
          </Button>
          <Button
            onClick={handleClear}
            variant="outlined"
            sx={{ width: "140px", borderRadius: "50px" }}
          >
            Clear Code
          </Button>
        </Box>
  
        <Box className={styles.codeInputs}>
          {code.map((digit, index) => (
            <input
              key={index}
              ref={(el) => (inputRefs.current[index] = el)}
              type="tel"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={1}
              value={digit}
              onChange={(e) => handleChangeCode(index, e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Backspace" && !code[index] && index > 0) {
                  inputRefs.current[index - 1]?.focus();
                }
              }}
              onPaste={index === 0 ? handlePaste : undefined} // paste only on first
              className={styles.codeInput}
            />
          ))}
        </Box>
  
        <LoaderButton
          isProcessing={isProcessing}
          onClick={handleConfirmCode}
          confirmText="Confirm"
          borderRadius="50px"
          width="280px"
        />
      </>
    );
  };

  return (
    <>
      <Box className={[styles.signIn, className].join(" ")} sx={{ width: isMobile ? "90%" : "800px" }}>
        {isCodeConfirmation ? <CodeConfirmationScreen /> : <SignInScreen />}
      </Box>

      <Snackbar
        open={!!errorMessage}
        autoHideDuration={4000}
        onClose={() => setErrorMessage("")}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
        <Alert severity="error" onClose={() => setErrorMessage("")}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

SignIn.propTypes = {
  className: PropTypes.string,
  onClose: PropTypes.func,
  onSignInSuccess: PropTypes.func,
  emailPrefill: PropTypes.string,
};

export default SignIn;